/*
 * Copyright 2026 Sony Corporation
 */

import type { Vec3 } from '@ips-device-verse/twin-service/schema/scene-doc';

/**
 * The device vocabulary, from the one place that defines it.
 *
 * Shared with the agent's tool signatures for the same reason the scene ops
 * are: a hand-kept copy of an op's shape is a second definition, and a second
 * definition disagrees (devplan §3.2).
 */
export * from '@ips-device-verse/twin-service/schema/device-ops';
export { Vec3Schema } from '@ips-device-verse/twin-service/schema/scene-doc';
export type { Vec3 };

/**
 * The rotation that points a device at a target.
 *
 * Babylon's Euler triple is (pitch, yaw, roll) and its devices look down +Z,
 * which is also where a rig's lens points — so this is the same maths
 * `spawnTransform` does for a device facing the middle of the scene.
 *
 * Here rather than in the shared schema: it is how the browser *applies* an op,
 * not part of what an op is.
 */
export const lookAtRotation = (from: Vec3, target: Vec3): Vec3 => {
  const dx = target[0] - from[0];
  const dy = target[1] - from[1];
  const dz = target[2] - from[2];
  const flat = Math.hypot(dx, dz);

  return [-Math.atan2(dy, flat), Math.atan2(dx, dz), 0];
};
